import React from 'react';
import { Order } from '../../api/orders';
import { colors, spacing, typography, typographyFlat, borderRadius, colorExtensions } from '../../styles/tokens';
import { formatInstant } from '../../utils/datetime';
import { fulfillmentStatusLabel } from '../../utils/orderStatus';

interface OrderListProps {
  orders: Order[];
  isLoading: boolean;
  onOrderClick: (order: Order) => void;
  bare?: boolean;
}

export const OrderList: React.FC<OrderListProps> = ({ orders, isLoading, onOrderClick, bare = false }) => {
  if (isLoading) {
    return (
      <div style={bare ? { padding: spacing.xl, textAlign: 'center' } : {
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: borderRadius.lg,
        padding: spacing.xl,
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: typography.body.fontSize,
          color: colors.textSecondary
        }}>
          Loading orders...
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div style={bare ? { padding: spacing.xl, textAlign: 'center' } : {
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: borderRadius.lg,
        padding: spacing.xl,
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: typography.h2.fontSize,
          marginBottom: spacing.md
        }}>
          📦
        </div>
        <div style={{
          fontSize: typography.body.fontSize,
          color: colors.text,
          marginBottom: spacing.sm
        }}>
          No orders found
        </div>
        <div style={{
          fontSize: typography.small.fontSize,
          color: colors.textSecondary
        }}>
          Import orders from your sales channels to get started
        </div>
      </div>
    );
  }

  const formatDateTime = (dateString: string) => formatInstant(dateString);

  const formatAmount = (amount: string) => {
    const num = parseFloat(amount);
    return `$${num.toFixed(2)}`;
  };

  const getSourceBadgeColor = (source: string) => {
    const colors_map: Record<string, { bg: string; text: string }> = {
      tcgplayer: { bg: `${colors.info}20`, text: colorExtensions.infoDark },
      shopify: { bg: `${colors.success}19`, text: colorExtensions.successDark },
      ebay: { bg: `${colors.warning}26`, text: colorExtensions.warningDark },
      manapool: { bg: `${colors.secondary}1A`, text: colors.secondary },
    };
    return colors_map[source.toLowerCase()] || { bg: colors.backgroundAlt, text: colors.text };
  };

  const formatStatus = (status?: string) => fulfillmentStatusLabel(status);

  const getStatusBadgeColor = (status?: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'picked') {
      return { bg: `${colors.secondary}1A`, text: colors.secondaryDark };
    }
    if (['shipped', 'delivered'].includes(normalized)) {
      return { bg: `${colors.success}19`, text: colorExtensions.successDark };
    }
    if (normalized === 'replaced') {
      return { bg: `${colors.warning}26`, text: colorExtensions.warningDark };
    }
    if (normalized === 'cancelled') {
      return { bg: `${colors.error}19`, text: colorExtensions.errorDark };
    }
    if (['on_hold', 'error_retry'].includes(normalized)) {
      return { bg: `${colors.warning}26`, text: colorExtensions.warningDark };
    }
    return { bg: `${colors.primary}14`, text: colors.primaryDark };
  };

  return (
    <div style={bare ? { overflow: 'hidden' } : {
      backgroundColor: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.lg,
      overflow: 'hidden'
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}>
        <thead>
          <tr style={{
            backgroundColor: colors.backgroundAlt,
            borderBottom: `1px solid ${colors.border}`
          }}>
            <th style={{
              padding: `${spacing.sm} ${spacing.sm}`,
              textAlign: 'left',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              width: '92px',
            }}>
              Date
            </th>
            <th style={{
              padding: `${spacing.sm} ${spacing.sm}`,
              textAlign: 'left',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Order ID
            </th>
            <th style={{
              padding: spacing.md,
              textAlign: 'left',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Status
            </th>
            <th style={{
              padding: spacing.md,
              textAlign: 'left',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Source
            </th>
            <th style={{
              padding: spacing.md,
              textAlign: 'left',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Buyer
            </th>
            <th style={{
              padding: spacing.md,
              textAlign: 'right',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Items
            </th>
            <th style={{
              padding: spacing.md,
              textAlign: 'right',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const sourceBadge = getSourceBadgeColor(order.source);
            const displayStatus =
              (order.commercial_status || '').toLowerCase() === 'replaced'
                ? 'replaced'
                : order.fulfillment_status;
            const statusBadge = getStatusBadgeColor(displayStatus);
            
            return (
              <tr
                key={order.id}
                onClick={() => onOrderClick(order)}
                style={{
                  borderBottom: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.backgroundAlt;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={{
                  padding: `${spacing.sm} ${spacing.sm}`,
                  fontSize: typography.small.fontSize,
                  color: colors.text
                }}>
                  {formatDateTime(order.order_timestamp)}
                </td>
                <td style={{ padding: `${spacing.sm} ${spacing.sm}` }}>
                  <span style={{
                    fontSize: typography.body.fontSize,
                    color: colors.text,
                    fontFamily: 'monospace'
                  }}>
                    {order.display_id || order.external_id}
                  </span>
                </td>
                <td style={{
                  padding: spacing.md,
                }}>
                  <span style={{
                    display: 'inline-block',
                    padding: `${spacing.xs} ${spacing.sm}`,
                    backgroundColor: statusBadge.bg,
                    color: statusBadge.text,
                    fontSize: typography.small.fontSize,
                    fontWeight: typographyFlat.fontWeight.medium,
                    borderRadius: borderRadius.sm,
                    textTransform: 'capitalize'
                  }}>
                    {formatStatus(displayStatus)}
                  </span>
                  {order.ban_hold && (
                    <span style={{
                      display: 'inline-block',
                      marginLeft: spacing.xs,
                      padding: `${spacing.xs} ${spacing.sm}`,
                      backgroundColor: `${colors.error}19`,
                      color: colors.error,
                      fontSize: typography.small.fontSize,
                      fontWeight: typographyFlat.fontWeight.medium,
                      borderRadius: borderRadius.sm,
                    }}>
                      Banned
                    </span>
                  )}
                  {(order.short_picks?.length ?? 0) > 0 && (
                    <span style={{
                      display: 'inline-block',
                      marginLeft: spacing.xs,
                      padding: `${spacing.xs} ${spacing.sm}`,
                      backgroundColor: `${colors.warning}26`,
                      color: colorExtensions.warningDark,
                      fontSize: typography.small.fontSize,
                      fontWeight: typographyFlat.fontWeight.medium,
                      borderRadius: borderRadius.sm,
                    }}>
                      Short pick
                    </span>
                  )}
                  {order.is_repeat_customer && (
                    <span
                      title={`${order.prior_order_count || 0} prior order(s)`}
                      style={{
                        display: 'inline-block',
                        marginLeft: spacing.xs,
                        padding: `${spacing.xs} ${spacing.sm}`,
                        backgroundColor: `${colors.primary}14`,
                        color: colors.primaryDark,
                        fontSize: typography.small.fontSize,
                        fontWeight: typographyFlat.fontWeight.medium,
                        borderRadius: borderRadius.sm,
                      }}
                    >
                      Repeat{typeof order.prior_order_count === 'number' ? ` · ${order.prior_order_count}` : ''}
                    </span>
                  )}
                </td>
                <td style={{ padding: spacing.md }}>
                  <span style={{
                    display: 'inline-block',
                    padding: `${spacing.xs} ${spacing.sm}`,
                    backgroundColor: sourceBadge.bg,
                    color: sourceBadge.text,
                    fontSize: typography.small.fontSize,
                    fontWeight: typographyFlat.fontWeight.medium,
                    borderRadius: borderRadius.sm,
                    textTransform: 'capitalize'
                  }}>
                    {order.source}
                  </span>
                </td>
                <td style={{
                  padding: spacing.md,
                  fontSize: typography.body.fontSize,
                  color: colors.text
                }}>
                  {order.buyer_name || <span style={{ color: colors.textSecondary }}>—</span>}
                </td>
                <td style={{
                  padding: spacing.md,
                  fontSize: typography.body.fontSize,
                  color: colors.text,
                  textAlign: 'right'
                }}>
                  {order.item_count}
                </td>
                <td style={{
                  padding: spacing.md,
                  fontSize: typography.body.fontSize,
                  color: colors.text,
                  textAlign: 'right',
                  fontWeight: typographyFlat.fontWeight.semibold
                }}>
                  {formatAmount(order.total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
